import React, { useState } from 'react';
import { Loader, CheckCircle, AlertCircle, Trash2, Download } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileWord, faFilePdf, faFileAlt, faFilePowerpoint, faFileImage, faFileExcel, faFileVideo, faFile } from '@fortawesome/free-solid-svg-icons';
import { FileListProps, UploadedFile } from './types';
import { downloadFile } from '../../services/api/virtualAssistant';
import CustomModal from '../../components/CustomModal';

const FileList: React.FC<FileListProps> = ({ uploadedFiles, onRemoveFile, assistant }) => {
  const [downloadModal, setDownloadModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'loading'
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'docx':
        return faFileWord;
      case 'pdf':
        return faFilePdf;
      case 'txt':
        return faFileAlt;
      case 'pptx':
        return faFilePowerpoint;
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'bmp':
      case 'heif':
      case 'tiff':
      case 'tif':
        return faFileImage;
      case 'xlsx':
      case 'xls':
        return faFileExcel;
      case 'flv':
      case 'asf':
      case 'wmv':
      case 'qt':
      case 'mov':
      case 'mpg':
      case 'mpeg':
      case 'mpe':
      case 'avi':
      case 'm4v':
      case 'mp4':
      case '3gp':
        return faFileVideo;
      default:
        return faFile;
    }
  };

  const handleFileDownload = async (file: UploadedFile) => {
    if (!file.file_unique_id || !assistant?.app_id || file.status !== 'completed') {
      return;
    }

    // Show loading modal
    setDownloadModal({
      isOpen: true,
      title: 'Downloading File',
      message: `Downloading ${file.file_name}...`,
      type: 'loading'
    });

    try {
      const blob = await downloadFile(assistant.app_id, file.file_unique_id);
      
      // Hide loading modal
      setDownloadModal(prev => ({ ...prev, isOpen: false }));
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      
      // Show error modal
      setDownloadModal({
        isOpen: true,
        title: 'Download Failed',
        message: `Failed to download ${file.file_name}. Please try again.`,
        type: 'error'
      });
    }
  };

  if (uploadedFiles.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">Uploaded Files</h3>
        <div className="space-y-3">
          {uploadedFiles.map((file, index) => (
            <div 
              key={file.id || index} 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3 flex-1">
                <FontAwesomeIcon icon={getFileIcon(file.file_name)} className="h-5 w-5 text-gray-500" />
                {file.status === 'uploading' && (
                  <Loader className="h-5 w-5 text-blue-500 animate-spin" />
                )}
                {file.status === 'completed' && (
                  <CheckCircle className="h-5 w-5 text-black" />
                )}
                {file.status === 'error' && (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.file_name}
                  </p>
                  {/* <p className="text-sm text-gray-500">
                    {formatFileSize(file.size)}
                  </p> */}
                  {file.error && (
                    <p className="text-sm text-red-500">{file.error}</p>
                  )}
                </div>
              </div>

              {file.status === 'uploading' && (
                <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-4">
                  <div 
                    className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${file.progress}%` }}
                  ></div>
                </div>
              )}

              <div className="flex items-center space-x-1">
                {file.status === 'completed' && file.file_unique_id && assistant?.app_id && (
                  <button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleFileDownload(file);
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    title="Download file"
                  >
                    <Download className="h-5 w-5 text-gray-500" />
                  </button>
                )}
                
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onRemoveFile(file.sequence);
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <Trash2 className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Download Modal */}
      <CustomModal
        isOpen={downloadModal.isOpen}
        onClose={() => setDownloadModal(prev => ({ ...prev, isOpen: false }))}
        title={downloadModal.title}
        message={downloadModal.message}
        type={downloadModal.type}
        allowClose={downloadModal.type !== 'loading'}
      />
    </div>
  );
};

export default FileList;